import Cookies from 'js-cookie';

import getEnv, { Environment } from './environment';

export const PORTAL_AUTH_COOKIE_NAMES = {
  accessToken: 'access-token',
  refreshToken: 'refresh-token',
} as const;

export const PORTAL_AUTH_COOKIE_DOMAIN: Record<
  Environment,
  string | undefined
> = {
  [Environment.LOCAL]: undefined,
  [Environment.DEVMAJ]: '.sunrundev.com',
  [Environment.MAJSTG]: '.sunrun.com',
  [Environment.PROD]: '.sunrun.com',
};

export type PortalAuthTokens = {
  accessToken: string;
  refreshToken: string;
};

export function getPortalAuthCookieDomain(
  env: Environment = getEnv()
): string | undefined {
  return PORTAL_AUTH_COOKIE_DOMAIN[env];
}

type PortalAuthCookieOptions = {
  path: '/';
  sameSite: 'Lax';
  domain?: string;
};

function getPortalAuthCookieOptions(
  domain: string | undefined
): PortalAuthCookieOptions {
  return {
    path: '/',
    sameSite: 'Lax',
    ...(domain ? { domain } : {}),
  };
}

function setPortalAuthCookie(
  name: string,
  value: string,
  domain: string | undefined
) {
  Cookies.set(name, value, getPortalAuthCookieOptions(domain));
}

function removePortalAuthCookie(name: string, domain: string | undefined) {
  Cookies.remove(name, getPortalAuthCookieOptions(domain));
}

type SetPortalAuthCookiesOptions = {
  env?: Environment;
};

export function setPortalAuthCookies(
  tokens: PortalAuthTokens,
  { env }: SetPortalAuthCookiesOptions = {}
) {
  const domain = getPortalAuthCookieDomain(env);

  setPortalAuthCookie(
    PORTAL_AUTH_COOKIE_NAMES.accessToken,
    tokens.accessToken,
    domain
  );
  setPortalAuthCookie(
    PORTAL_AUTH_COOKIE_NAMES.refreshToken,
    tokens.refreshToken,
    domain
  );
}

export function clearPortalAuthCookies({
  env,
}: SetPortalAuthCookiesOptions = {}) {
  const domain = getPortalAuthCookieDomain(env);

  removePortalAuthCookie(PORTAL_AUTH_COOKIE_NAMES.accessToken, domain);
  removePortalAuthCookie(PORTAL_AUTH_COOKIE_NAMES.refreshToken, domain);
}

export function getPortalAuthCookies(): PortalAuthTokens | undefined {
  const accessToken = Cookies.get(PORTAL_AUTH_COOKIE_NAMES.accessToken);
  const refreshToken = Cookies.get(PORTAL_AUTH_COOKIE_NAMES.refreshToken);

  if (!accessToken || !refreshToken) {
    return undefined;
  }

  return { accessToken, refreshToken };
}

/** @deprecated Use setPortalAuthCookies */
export const setLegacyAuthCookies = setPortalAuthCookies;

/** @deprecated Use clearPortalAuthCookies */
export const clearLegacyAuthCookies = clearPortalAuthCookies;

/** @deprecated Use getPortalAuthCookies */
export const getLegacyAuthCookies = getPortalAuthCookies;

/** @deprecated Use getPortalAuthCookieDomain */
export const getLegacyAuthCookieDomain = getPortalAuthCookieDomain;

/** @deprecated Use PORTAL_AUTH_COOKIE_NAMES */
export const LEGACY_AUTH_COOKIE_NAMES = PORTAL_AUTH_COOKIE_NAMES;

/** @deprecated Use PORTAL_AUTH_COOKIE_DOMAIN */
export const LEGACY_AUTH_COOKIE_DOMAIN = PORTAL_AUTH_COOKIE_DOMAIN;

export type LegacyAuthTokens = PortalAuthTokens;

export function redirectWithAuthHandoff(url: string) {
  window.location.assign(url);
}

/** @deprecated Use redirectWithAuthHandoff */
export const redirectToLegacyMySunrun = redirectWithAuthHandoff;
